/** Provider switch invalidation for Canvas Generate Image nodes. */

import type { ImageGenerationProviderChannel } from '../../../api'

/** Canonical persisted fields when the user picks a provider channel. */
export function buildImageProviderSelectionPatch(
  channelId: string,
  channels: ImageGenerationProviderChannel[],
): Record<string, unknown> {
  const channel = channels.find((c) => c.id === channelId)
  return {
    providerChannel: channelId,
    modelId: channel?.model_id,
    providerId: channel?.provider_id,
    providerRoutePolicy: channel?.route_policy ?? null,
  }
}

/** Reset FAILED execution context after provider switch — never clears prompts/assets. */
export function buildImageProviderSwitchExecutionResetPatch(args: {
  hasPersistedResult: boolean
  currentStatus: string
}): Record<string, unknown> | null {
  if (args.currentStatus !== 'FAILED') return null
  return { status: args.hasPersistedResult ? 'COMPLETED' : 'READY' }
}

export function shouldClearImageGenerationFailedRunIdentity(args: {
  currentStatus: string
  taskFailed: boolean
}): boolean {
  return args.currentStatus === 'FAILED' || args.taskFailed
}

/** Monotonic attempt identity — incremented on provider switch and each new submit. */
export function nextImageGenerationAttemptId(current: number): number {
  return current + 1
}

/** Drop late async submit/hydration results from a superseded attempt. */
export function shouldDiscardImageGenerationAttempt(
  capturedAttemptId: number,
  currentAttemptId: number,
): boolean {
  return capturedAttemptId !== currentAttemptId
}

/**
 * @deprecated Provider-string comparison caused false-positive discards; use attempt identity.
 */
export function shouldDiscardImageGenerationProviderContext(
  capturedProviderChannel: string | undefined,
  currentProviderChannel: string | undefined,
): boolean {
  return capturedProviderChannel !== currentProviderChannel
}

const PROVIDER_DEPENDENT_SUBMIT_ERROR_CODES = new Set([
  'REFERENCE_REQUIRES_CLOUD_PROVIDER',
  'FAST_REFERENCE_UNSUPPORTED',
  'REFERENCE_LIMIT_EXCEEDED',
  'TOO_MANY_REFERENCE_IMAGES',
  'PROVIDER_EXECUTION_NOT_SUPPORTED',
  'IMAGE_GENERATION_ROUTE_UNAVAILABLE',
  'PROVIDER_NOT_ENABLED_FOR_FUNCTION_NODE',
  'MODEL_NOT_BOUND_TO_FUNCTION_NODE',
  'PROVIDER_CHANNEL_MISMATCH',
  'CLOUD_PROVIDER_NOT_ENTITLED',
  'CLOUD_FALLBACK_NOT_ALLOWED',
  'PROMPT_EXCEEDS_PROVIDER_LIMIT',
])

export function isProviderDependentImageSubmitError(code: string | null | undefined): boolean {
  const normalized = (code || '').trim().toUpperCase()
  if (!normalized) return false
  return PROVIDER_DEPENDENT_SUBMIT_ERROR_CODES.has(normalized)
}

/**
 * Current-attempt ownership guard for hydrating a persisted `latestRunId`'s
 * Run/Task on mount/reload (VistoeraTASK-00143 P6/P7 — "HISTORY != CURRENT
 * EXECUTION STATE"). A Run/Task only represents the node's CURRENT
 * status/error when it was submitted under the SAME provider channel the
 * node currently has selected (`fields.lastSubmittedGenerationParameters.
 * providerChannel` vs the live `fields.providerChannel`). If the provider
 * was switched after that Run was submitted — regardless of whether the
 * switch-time persisted reset (`buildImageProviderSwitchExecutionResetPatch`
 * + `onChangeLatestRunId(id, null)`) already fired, raced with autosave, or
 * never reached the backend before a refresh — the historical Run must
 * never re-surface as the current node status/error/loading state. It
 * remains visible only in Generation History; it must never block a new
 * attempt on the now-current provider.
 *
 * Missing snapshots (older nodes persisted before this fingerprint field
 * existed) default to "owned" — never retroactively hide legitimate history
 * for nodes this field never covered.
 */
export function isImageGenerationRunOwnedByCurrentProvider(args: {
  submittedProviderChannel: string | null | undefined
  currentProviderChannel: string | null | undefined
}): boolean {
  if (!args.submittedProviderChannel || !args.currentProviderChannel) return true
  return args.submittedProviderChannel === args.currentProviderChannel
}
